import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import BackToTop from './components/BackToTop';
import useReveal from './hooks/useReveal';
import Home from './pages/Home';
import Products from './pages/Products';
import Services from './pages/Services';
import Franchise from './pages/Franchise';
import About from './pages/About';
import Contact from './pages/Contact';
import NotFound from './pages/NotFound';
import './styles/motion.css';

/** Resets scroll on navigation, but leaves in-page #anchor jumps alone. */
const ScrollToTop: React.FC = () => {
    const { pathname, hash } = useLocation();

    useEffect(() => {
        if (hash) {
            document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
            return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [pathname, hash]);

    return null;
};

const App: React.FC = () => {
    const { pathname } = useLocation();

    useReveal();

    return (
        <div className="app-shell">
            <ScrollToTop />
            <ScrollProgress />
            <Header />

            <main id="main">
                {/* Keyed on the path so every navigation replays the page
                    transition and re-runs each page's reveal choreography. */}
                <div className="page-transition" key={pathname}>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/products" element={<Products />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/franchise" element={<Franchise />} />
                        <Route path="/about" element={<About />} />
                        <Route path="/contact" element={<Contact />} />
                        {/* Legacy URL kept alive so existing links and indexed pages resolve. */}
                        <Route path="/technology" element={<Navigate to="/services" replace />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </div>
            </main>

            <Footer />
            <BackToTop />
        </div>
    );
};

export default App;
